// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.
import { createSelector } from 'reselect'
import { groupBy, sortBy } from 'lodash'

import { fullFileStateSelector } from './fullFileFirstOrder'
import { positionReset } from '../helpers/lists'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Note} Note
 */

/**
 * @type {function(PlottrState): Array<Note>}
 */
export const allNotesSelector = createSelector(fullFileStateSelector, ({ notes }) => notes ?? [])

const selectId = (_state, id) => id

/**
 * @type {function(PlottrState): Array<Note>}
 */
export const sortedNotesSelector = createSelector(allNotesSelector, (notes) =>
  sortBy(notes, ['title', 'id'])
)

/**
 * @type {function(PlottrState, number): (Note | Null)}
 */
export const singleNoteSelector = createSelector(allNotesSelector, selectId, (notes, propId) => {
  return notes.find((n) => n.id == propId) || null
})

/**
 * @type {function(PlottrState): Record<String, Array<Note>>}
 */
export const notesByCategorySelector = createSelector(allNotesSelector, (notes) => {
  const notesWithCategory = notes.map((note) => {
    if (!note.categoryId) {
      return {
        ...note,
        categoryId: null,
      }
    }
    return note
  })
  const grouped = groupBy(notesWithCategory, 'categoryId')
  // NOTE(ed): This fixes a very old bug with categories.  It probably
  // doesn't exist in any user files anymore, but we'll keep it around
  // just to be safe.
  //
  // @ts-ignore
  if (grouped[undefined] !== undefined) {
    // @ts-ignore
    grouped['null'] = grouped[undefined].concat(grouped['null'] || [])
    delete grouped['undefined']
  }

  const groupWithPosition = Object.values(grouped).map((group) => {
    return positionReset(group)
  })
  return groupBy(groupWithPosition.flat(), 'categoryId')
})

/**
 * @type {function(PlottrState): Record<number, String>}
 */
export const stringifiedNotesByIdSelector = createSelector(allNotesSelector, (notes) => {
  return notes.reduce((acc, note) => {
    return {
      ...acc,
      [note.id]: JSON.stringify(note).toLowerCase(),
    }
  }, {})
})
